<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getTimeZoneOffsetMinutes } from '@bc-library/common'
import BAlert from '@/components/common/BAlert.vue'
import { timeZoneApi } from '@/services/api'
import { useUserStore } from '@/config/store/userStore'

type TMismatch = {
  timeZone: string
  browserOffset: number
  offset: number
}

const { t } = useI18n()
const userStore = useUserStore()

// A browser (or system) with outdated time zone rules reads every time typed in the app with the
// wrong offset (Casablanca at UTC+1 after Morocco moved to UTC+0): checked against the API's rules.
const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

const officeTimeZone = computed(() => {
  const officeId = userStore.user?.currentJob?.officeId
  if (!officeId)
    return undefined
  return userStore.firmContext.offices.find(office => office._id === officeId)?.timeZonePopulated?.timeZone
})

const mismatch = ref<TMismatch | null>(null)
const isDismissed = ref(false)

const formatOffset = (minutes: number) => {
  const hours = Math.floor(Math.abs(minutes) / 60)
  const rest = Math.abs(minutes) % 60
  return `UTC${minutes < 0 ? '−' : '+'}${hours}${rest ? `:${String(rest).padStart(2, '0')}` : ''}`
}

const findMismatch = async (zones: string[]): Promise<TMismatch | null> => {
  const { at, offsets } = await timeZoneApi.getOffsets(zones).promise
  const instant = new Date(at)

  for (const timeZone of zones) {
    const offset = offsets[timeZone]
    if (offset === undefined)
      continue

    // The browser's own zone is the one dates typed in the app are read in
    const browserOffset = timeZone === browserTimeZone
      ? -instant.getTimezoneOffset()
      : getTimeZoneOffsetMinutes(timeZone, instant)
    if (browserOffset !== null && browserOffset !== offset)
      return { timeZone, browserOffset, offset }
  }
  return null
}

let checkId = 0
watch([() => userStore.user?._id, officeTimeZone], async ([userId, officeZone]) => {
  const id = ++checkId
  if (!userId) {
    mismatch.value = null
    return
  }

  const zones = [...new Set([browserTimeZone, officeZone].filter((zone): zone is string => Boolean(zone)))]
  try {
    const found = await findMismatch(zones)
    if (id === checkId)
      mismatch.value = found
  }
  catch {
    // A failed check says nothing about the browser: no warning
  }
}, { immediate: true })

const alert = computed(() => mismatch.value && {
  title: t('common.timeZoneRules.title'),
  message: t('common.timeZoneRules.message', {
    timeZone: mismatch.value.timeZone,
    browserOffset: formatOffset(mismatch.value.browserOffset),
    offset: formatOffset(mismatch.value.offset),
  }),
  color: 'warning' as const,
  duration: 0,
})
</script>

<template>
  <BAlert
    v-if="alert && !isDismissed"
    :alert="alert"
    size="normal"
    class="mx-3 mt-3 print:hidden"
    @close="isDismissed = true"
  />
</template>
